import {useCallback, useMemo, useRef, useState} from 'react'
import type {PickerOption} from '@shared/components/base/inputs/Picker'

/** One page of a list endpoint. */
type Page<T> = {count: number; results: T[]}

type PageQuery<T> = (args: {search: string; limit: number}) => {
  data?: Page<T>
  isFetching: boolean
  // Optional: page hooks that pass these through get an error row with a retry.
  isError?: boolean
  refetch?: () => unknown
}

/**
 * How a row becomes an option, and whether it is shown at all.
 *
 * `toOption` is for rows that are not already `{id, name}` — a user is
 * first_name/last_name/email, a rule carries far more than its name. Omit it
 * and the row's own `name` is used.
 *
 * `keep` hides rows from the list (e.g. master resource groups) without
 * disturbing paging.
 */
type Shaping<T> = {
  toOption?: (row: T) => PickerOption
  keep?: (row: T) => boolean
  /**
   * Change this and the search term and page size start over.
   *
   * For a picker whose underlying list is swapped out from under it — the
   * rollup form's signal scope widens from one equipment to the whole tenant —
   * the term was typed against the previous list, and carrying it over silently
   * hides most of what the new one just made available.
   */
  resetKey?: string
}

export const OPTIONS_PAGE_SIZE = 25

/**
 * Everything a `Picker` needs to show, search and grow the list — plus the raw
 * rows behind it, for callers that need more than `{id, name}` (a rollup's
 * `is_additive` decides the shape of the formula it goes into). Spreading the
 * whole thing onto a Picker is still fine: JSX ignores the extra key.
 */
type Result<T> = {
  rows: T[]
  options: PickerOption[]
  /** How many rows the server has in total, for a "showing x of y" hint. */
  count: number
  isLoading: boolean
  isLoadingMore: boolean
  hasMore: boolean
  onSearch: (term: string) => void
  onLoadMore: () => void
  // Optional so hand-built results (e.g. test doubles) need not supply them.
  isError?: boolean
  onRetry?: () => void
}

/**
 * Feeds a `Picker` from a paged, server-searched endpoint.
 *
 * The alternative — fetch one big page and filter it in the browser — is only
 * correct while the whole list fits in that page. Past it the dropdown simply
 * ends, and because the filtering is local, the rows beyond it cannot be found
 * by typing either: the search box silently claims they do not exist. Raising
 * the page size moves that cliff without removing it, so the search goes to the
 * server instead and the list grows on demand.
 *
 * "Load more" grows the limit rather than fetching the next offset and
 * appending it. One request always holds the whole answer, so there is no
 * accumulated copy to keep in step with it — nothing to dedupe, nothing to
 * reset when the term changes, and no chance of pinning a stale page underneath
 * a new search. The cost is refetching rows already held, which is not worth
 * optimising on a dropdown. Same approach as the production area field on the
 * Line Information page.
 *
 * `keep` filters rows out for display (e.g. master resource groups) without
 * disturbing paging: "is there more" is answered from what the server counted
 * and what it has actually sent, never from the filtered view — otherwise a
 * list whose every page loses a row would offer "load more" forever.
 *
 * Pass `toOption` when the rows are not already `{id, name}`.
 */
export const usePagedOptions = <T extends {id: string | number}>(
  usePageQuery: PageQuery<T>,
  {toOption, keep = () => true, resetKey}: Shaping<T> = {},
): Result<T> => {
  const [search, setSearch] = useState('')
  const [limit, setLimit] = useState(OPTIONS_PAGE_SIZE)

  // Adjusting state during render rather than in an effect: React applies it
  // before committing, so the query below already sees the cleared term instead
  // of firing once against the old one and again after.
  const lastResetKey = useRef(resetKey)
  if (lastResetKey.current !== resetKey) {
    lastResetKey.current = resetKey
    setSearch('')
    setLimit(OPTIONS_PAGE_SIZE)
  }

  const {data, isFetching, isError = false, refetch} = usePageQuery({search, limit})
  const loaded = data?.results ?? []

  const handleSearch = useCallback((term: string): void => {
    setSearch(term)
    // A new term is a new list, so it starts at one page again.
    setLimit(OPTIONS_PAGE_SIZE)
  }, [])

  const handleLoadMore = useCallback((): void => {
    setLimit((current) => current + OPTIONS_PAGE_SIZE)
  }, [])

  const options = useMemo(
    () =>
      loaded.filter(keep).map(
        (row) =>
          toOption?.(row) ?? {
            id: String(row.id),
            // Reached only when the caller supplied no toOption, which it
            // should do unless the row already carries a name.
            name: (row as unknown as {name?: string}).name ?? String(row.id),
          },
      ),
    // Both callbacks are written inline by callers, so they have a new identity
    // every render — depending on them would rebuild this list on each one.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loaded],
  )

  return {
    rows: loaded,
    options,
    count: data?.count ?? 0,
    isLoading: isFetching && limit === OPTIONS_PAGE_SIZE,
    isLoadingMore: isFetching && limit > OPTIONS_PAGE_SIZE,
    hasMore: loaded.length < (data?.count ?? 0),
    onSearch: handleSearch,
    onLoadMore: handleLoadMore,
    isError,
    onRetry: (): void => {
      refetch?.()
    },
  }
}
